import { cn } from '@/context/global/utils/cn';
import React from 'react';

export interface IButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  onClick?: () => void;
  variant?: 'primary' | 'white' | 'dark' | 'gray';
  disabled?: boolean;
  title?: string;
  icon?: React.ReactNode;
  iconClassname?: string;
  suffix?: React.ReactNode;
  loading?: boolean;
  outline?: boolean;
  parentClassname?: string;
}

const baseClasses =
  'flex w-full min-w-20 items-center justify-center gap-2 text-[18px] cursor-pointer rounded-md font-semibold transition-all duration-200  disabled:cursor-not-allowed disabled:opacity-60';

const filledClasses: Record<NonNullable<IButtonProps['variant']>, string> = {
  primary: 'bg-ais-green text-white hover:bg-ais-green/90  text-black',
  white: 'bg-white text-black hover:bg-white/90',
  dark: 'bg-white text-white border bg-black border-white hover:bg-white hover:text-black hover:border-black',
  gray: 'bg-white text-white border bg-black border-white/10 hover:bg-white hover:text-black hover:border-black',
};

const outlineClasses: Record<NonNullable<IButtonProps['variant']>, string> = {
  primary: 'border border-ais-green text-white hover:bg-ais-green focus:ring-ais-green ',
  white: 'border border-gray-300 text-white hover:bg-gray-50 hover:text-black ',
  dark: 'bg-white text-white bg-black border border-white hover:bg-gray-300 ',
  gray: 'bg-white text-white bg-black border border-white/10 hover:bg-gray-300 hover:text-black ',
};

const spinnerBorderClass: Record<NonNullable<IButtonProps['variant']>, string> = {
  primary: 'border-ais-green',
  white: 'border-gray-300',
  dark: 'border-gray-800',
  gray: 'border-gray-500',
};

const CWButton = ({
  className,
  onClick,
  variant = 'primary',
  disabled = false,
  title,
  icon,
  suffix,
  outline = false,
  loading = false,
  parentClassname,
  iconClassname,
  ...rest
}: IButtonProps) => {
  const isDisabled = disabled || loading;

  const variantClass = outline ? outlineClasses[variant] : filledClasses[variant];
  const spinnerClass = spinnerBorderClass[variant];

  return (
    <div className={cn('relative block', parentClassname)}>
      <button
        className={cn(baseClasses, variantClass, className)}
        onClick={!isDisabled ? onClick : undefined}
        disabled={isDisabled}
        {...rest}
      >
        {loading && (
          <span
            className={cn(
              'inline-block h-[18px] w-[18px] shrink-0 animate-spin rounded-full border-2 border-l-transparent align-middle',
              outline ? spinnerClass : 'border-white',
            )}
          />
        )}
        {icon && !loading && <div className={cn('shrink-0', iconClassname)}>{icon}</div>}
        {title && !loading && title}
        {suffix && !loading && suffix}
      </button>
    </div>
  );
};

export default CWButton;
